import _ from 'lodash';
import { connect } from 'react-redux';
import React from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import { Cell, ComposableDataTable, Row } from 'widgets-v5/data-table';
import { EqualColumn, EqualColumnsLayout } from 'widgets-v5/equal-columns-layout';
import { GlobalModal } from 'widgets-v5/modal';
import { NeutralButton, PrimaryButton, SecondaryButton } from 'widgets-v5/buttons';
import { PanelsLayout, ScrollingSidebarPanel, WorkspacePanel } from 'widgets-v5/panels-layout';
import ClickToCopyButton from 'widgets/click-to-copy-button';
import Navigation, { NavigationItem, NavigationSeparator } from 'widgets-v5/navigation';
import Spacer from 'widgets-v5/spacer';
import StandardInput from 'widgets-v5/standard-input';
/** import widgets components in the section above, Sort Lines Ascending */

import {
    closeModal,
    impersonate,
    init,
    openModal,
    setOrganization,
    unimpersonate,
} from './actions';
/** import multi application imports in the section above, Sort Lines Ascending */

import { filterRecordsByKeywords } from 'states/resources/organizations/business-logic';
import { rolePresentationDictionary } from 'common/constants/role-presentation-dict';
import select from './selector';
/** import single application imports in the section above, Sort Lines Ascending */

/**
 * Filters organizations by matching the organization name/ID, visible parent
 * name, or any member's name/ID/email against the provided keywords.
 * Parent matching is name-only: a 24-character id matches only the org's own id,
 * not a parent id. Children with displayParentName still match parent name.
 * Member email: @ in input → case-insensitive substring on full email;
 * no @ → exact local or domain part (case-insensitive), plus name/id as usual.
 *
 * @param {Array<Object>} organizations - Array of organization objects to filter.
 * @param {string} [organizations[].id] - The ID of the organization.
 * @param {string} [organizations[].name] - The name of the organization.
 * @param {boolean} [organizations[].displayParentName] - When true, also match parent name.
 * @param {Object} [organizations[].parent] - Parent org used for name-only matching when `displayParentName` is true.
 * @param {string} [organizations[].parent.name]
 * @param {Array<Object>} [organizations[].users] - The list of members belonging to the organization.
 * @param {string} [organizations[].users[].id] - The ID of the member.
 * @param {string} [organizations[].users[].name] - The name of the member.
 * @param {string} [organizations[].users[].email] - The email of the member.
 * @param {string} keywords - The search keywords to filter by.
 * @returns {Array<Object>} The filtered array of organizations.
 */
export function filterOrganizationsByOrgOrMember(organizations, keywords) {
    if (!organizations || !Array.isArray(organizations)) {
        return [];
    }

    if (!keywords || keywords.trim() === '') {
        return organizations;
    }

    return organizations.filter(organization => {
        const orgNameRecords = [organization];
        if (organization.displayParentName && organization.parent?.name) {
            orgNameRecords.push({
                name: organization.parent.name,
            });
        }

        const matchesOrganization =
            filterRecordsByKeywords(orgNameRecords, keywords, {
                nameField: 'name',
                idField: 'id',
            }).length > 0;

        if (matchesOrganization) {
            return true;
        }

        // @ substring or exact local/domain email match (case-insensitive)
        return (
            filterRecordsByKeywords(organization.users || [], keywords, {
                nameField: 'name',
                idField: 'id',
                emailField: 'email',
            }).length > 0
        );
    });
}

function getImpersonationErrorMessage(error) {
    if (!error) {
        return null;
    }

    let errorMessage;

    if (_.isString(error)) {
        errorMessage = error;
    } else {
        const raw = _.get(error, 'message', '');
        try {
            const parsed = JSON.parse(raw);
            errorMessage =
                _.get(parsed, 'body.message') ||
                _.get(parsed, 'bodyText') ||
                _.get(parsed, 'message');
        } catch (e) {
            errorMessage = raw || _.get(error, 'body.message') || _.get(error, 'bodyText');
        }
    }

    return `Unable to impersonate, error: ${errorMessage || 'unknown error'}`;
}

class Impersonate extends React.Component {
    UNSAFE_componentWillMount() {}

    impersonate = userId => {
        this.props.dispatch(impersonate(userId));
    };

    unimpersonate = () => {
        this.props.dispatch(unimpersonate());
    };

    handleClose = () => {
        this.props.dispatch(closeModal());
        // Reset to own organization when closing the modal
        this.props.dispatch(setOrganization(this.props.userOrganizationId));
    };

    // Expose handleOpen for external usage (e.g., from window)
    componentDidMount() {
        window.handleOpenImpersonate = (opts = {}) => {
            if (opts.organizationFilter && this.modalRef && this.modalRef.setFilter) {
                this.modalRef.setFilter(opts.organizationFilter);
            }
            if (opts.selectedOrganization) {
                this.props.dispatch(setOrganization(opts.selectedOrganization));
            }
            this.handleOpen();
        };
    }

    componentWillUnmount() {
        if (window.handleOpenImpersonate) {
            delete window.handleOpenImpersonate;
        }
    }

    handleOpen = () => {
        this.props.dispatch(init());
        this.props.dispatch(openModal());
    };

    setOrganization = orgId => {
        this.props.dispatch(setOrganization(orgId));
    };

    render() {
        const {
            userFirstName,
            userOrganizationName,
            userOrganizationId,
            organizations,
            orgGroups,
            isImpersonating,
            isModalOpen,
            isLoading,
            currentUserId,
            selectedOrganization,
            errors,
            realPersonOrganization,
            realPersonId,
        } = this.props;

        if (!userFirstName || !userOrganizationName) {
            return (
                <div className="ef5-impersonate">
                    <i className="fa fa-spinner fa-spin ef5-impersonate_loading" />
                </div>
            );
        }

        const modal = (
            <ImpersonationModal
                ref={ref => {
                    this.modalRef = ref;
                }}
                organizations={organizations}
                orgGroups={orgGroups}
                selectedOrganization={selectedOrganization}
                isImpersonating={isImpersonating}
                realPersonOrganization={realPersonOrganization}
                realPersonId={realPersonId}
                userOrganizationId={userOrganizationId}
                handleClose={this.handleClose}
                impersonate={this.impersonate}
                unimpersonate={this.unimpersonate}
                setOrganization={this.setOrganization}
                isModalOpen={isModalOpen}
                currentUserId={currentUserId}
                isLoading={isLoading}
                errors={errors}
            />
        );

        if (this.props.isImpersonating) {
            return (
                <div className="ef5-impersonate ef5-impersonate_is-impersonating">
                    <span className="ef5-impersonate__status ef5-impersonate__status_is-impersonating">
                        Viewing page as:
                        <br />
                        {this.props.userFirstName} — {this.props.userOrganizationName}
                    </span>
                    <EqualColumnsLayout>
                        <EqualColumn>
                            <SecondaryButton
                                className="ef5-impersonate__control"
                                text="Switch"
                                onClick={this.handleOpen}
                            />
                        </EqualColumn>
                        <EqualColumn>
                            <SecondaryButton
                                className="ef5-impersonate__control"
                                text="Stop"
                                onClick={this.unimpersonate}
                            />
                        </EqualColumn>
                    </EqualColumnsLayout>
                    {modal}
                </div>
            );
        } else {
            return (
                <div className="ef5-impersonate">
                    <span className="ef5-impersonate__status">
                        Logged in as:
                        <br />
                        {this.props.userFirstName} — {this.props.userOrganizationName}
                    </span>
                    <EqualColumnsLayout>
                        <EqualColumn>
                            <SecondaryButton
                                className="ef5-impersonate__control"
                                text="Impersonate"
                                onClick={this.handleOpen}
                            />
                        </EqualColumn>
                    </EqualColumnsLayout>
                    {modal}
                </div>
            );
        }
    }
}

class UserRow extends React.Component {
    render() {
        const {
            currentUserId,
            selectedOrganization,
            realPersonOrganization,
            realPersonId,
            isImpersonating,
            userOrganizationId,
            unImpersonate,
            impersonate,
            handleClose,
            filter,
        } = this.props;

        const getIsCurrentUser = user => {
            return isImpersonating
                ? realPersonOrganization === user.organization && realPersonId === user.id
                : userOrganizationId === user.organization && currentUserId === user.id;
        };

        const getIsImpersonator = user => {
            return (
                isImpersonating &&
                userOrganizationId === user.organization &&
                currentUserId === user.id
            );
        };

        // It would be possible to pass the sort task to the database
        // which would improve the performance here a little bit more
        // but it honestly feels an overkill at this point. It is annoying looping
        // through the records twice in such a sort space tho...
        const sortedUsers = _.sortBy(selectedOrganization.users, user => user.name.toLowerCase());

        return (
            <>
                {sortedUsers.map(user => {
                    const isCurrentUser = getIsCurrentUser(user);
                    const isImpersonator = getIsImpersonator(user);
                    const showIdentityAction = isCurrentUser || isImpersonator;
                    const shouldStopImpersonating =
                        isImpersonating && (isCurrentUser || isImpersonator);
                    const trimmed = (filter || '').trim();
                    // Empty query matches every record in filterRecordsByKeywords.
                    const matchesSearch =
                        Boolean(trimmed) &&
                        // Same matcher as org/member search (partial name, email rules, exact 24-char id).
                        filterRecordsByKeywords([user], trimmed, {
                            nameField: 'name',
                            idField: 'id',
                            emailField: 'email',
                        }).length > 0;

                    return (
                        <Row
                            className={[
                                'ef5-impersonate-modal__row',
                                isImpersonator && 'ef5-impersonate-modal__row_is-impersonated-user',
                                showIdentityAction &&
                                    'ef5-impersonate-modal__row_has-identity-action',
                                matchesSearch && 'ef5-impersonate-modal__row_matches-search',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            key={user.id}
                            activeRow={user.id === this.props.currentUserId}
                        >
                            <Cell className="ef5-impersonate-modal__col ef5-impersonate-modal__col--primary">
                                {user.name}
                            </Cell>
                            <Cell className="ef5-impersonate-modal__col ef5-impersonate-modal__col--primary">
                                {user.email}
                            </Cell>
                            <Cell className="ef5-impersonate-modal__col ef5-impersonate-modal__col--primary">
                                {rolePresentationDictionary[user.global_role]}
                            </Cell>
                            <Cell className="ef5-impersonate-modal__col ef5-impersonate-modal__col--action">
                                <div className="ef5-impersonate-modal__action-row">
                                    {isCurrentUser || isImpersonator ? (
                                        <div className="ef5-impersonate-modal__action-button">
                                            <Tooltip
                                                title={
                                                    <Typography variant="body1">
                                                        {isCurrentUser
                                                            ? 'Return to your account'
                                                            : 'Stop impersonating'}
                                                    </Typography>
                                                }
                                            >
                                                <span>
                                                    <NeutralButton
                                                        text={
                                                            isCurrentUser ? 'Me' : 'Impersonating'
                                                        }
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            shouldStopImpersonating
                                                                ? unImpersonate()
                                                                : handleClose();
                                                        }}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </div>
                                    ) : (
                                        <div className="ef5-impersonate-modal__action-button ef5-impersonate-modal__impersonate-button">
                                            <Tooltip
                                                title={
                                                    <Typography variant="body1">
                                                        Impersonate this user
                                                    </Typography>
                                                }
                                            >
                                                <span>
                                                    <PrimaryButton
                                                        text="Impersonate"
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            impersonate(user.id);
                                                        }}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </div>
                                    )}
                                    <div className="ef5-impersonate-modal__user-copy-button">
                                        <ClickToCopyButton
                                            variant="icon"
                                            textToCopy={String(user.id)}
                                            textToDisplay="Copy user ID"
                                            beforeCopy={event => event.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            </Cell>
                        </Row>
                    );
                })}
            </>
        );
    }
}

class ImpersonationModal extends React.Component {
    state = {
        filter: '',
    };

    setFilter = filter => {
        this.setState({ filter: filter || '' });
    };

    handleClose = e => {
        this.setState({ filter: '' }, () => {
            this.props.handleClose(e);
        });
    };

    onFilterInput = value => {
        this.setState({ filter: value });
    };

    /**
     * Org sidebar label with copy control. When `displayParentName` is set,
     * the muted suffix comes from `parent.name`.
     *
     * @param {{ label: string, value: string, displayParentName: boolean, parent?: { name?: string } }} org
     * @returns {React.ReactNode}
     */
    renderOrgNavLabel = org => (
        <span className="ef5-impersonate-modal__org-label">
            <Tooltip
                title={<Typography variant="body1">View users in this organization</Typography>}
            >
                <span className="ef5-impersonate-modal__org-name">
                    {org.label}
                    {org.displayParentName ? (
                        <span className="ef5-impersonate-modal__org-parent-suffix">
                            {` (${org.parent.name})`}
                        </span>
                    ) : null}
                </span>
            </Tooltip>
            <span className="ef5-impersonate-modal__org-copy-button">
                <ClickToCopyButton
                    variant="icon"
                    textToCopy={String(org.value)}
                    textToDisplay="Copy organization ID"
                    beforeCopy={event => event.stopPropagation()}
                />
            </span>
        </span>
    );

    render() {
        var self = this;

        var contents;
        const latestError = _.last(this.props.errors);
        const errorMessage = getImpersonationErrorMessage(latestError);
        const errorBanner = errorMessage ? (
            <div className="ef5-impersonate-modal__error">{errorMessage}</div>
        ) : null;

        if (this.props.isLoading) {
            contents = <CircularProgress />;
        } else if (this.props.organizations.length === 0) {
            contents = (
                <>
                    {errorBanner}
                    <h2 className="ef3-impersonationModal_table_noOrgs">
                        You are not permitted to impersonate any users.
                    </h2>
                </>
            );
        } else {
            const selectedOrganizationIndex = _.findIndex(this.props.organizations, mapping => {
                return mapping.id === self.props.selectedOrganization;
            });

            const selectedOrganization = this.props.organizations[selectedOrganizationIndex];

            const filteredOrgGroups = (this.props.orgGroups || [])
                .map(group => ({
                    key: group.key,
                    label: group.label,
                    orgs: filterOrganizationsByOrgOrMember(group.orgs, this.state.filter).map(
                        org => ({
                            label: org.name,
                            value: org.id,
                            displayParentName: org.displayParentName,
                            parent: org.parent,
                        })
                    ),
                }))
                .filter(group => group.orgs.length > 0);

            contents = (
                <div className="ef5-impersonate-modal__contents">
                    {errorBanner}
                    <p>
                        Impersonating a user lets you see and interact with the console exactly as
                        they do.
                    </p>
                    <Spacer />
                    <StandardInput
                        value={this.state.filter}
                        onChange={this.onFilterInput}
                        placeholder="Search Organizations or Users by Name, Email, or ID"
                    />
                    <Spacer />
                    <PanelsLayout direction="row" className="ef5-impersonate-modal__table">
                        <ScrollingSidebarPanel className="ef5-impersonate-modal__organizations">
                            <Navigation activeItem={this.props.selectedOrganization}>
                                {filteredOrgGroups.map(group => (
                                    <React.Fragment key={group.key}>
                                        <NavigationSeparator label={group.label} />
                                        {group.orgs.map(org => (
                                            <NavigationItem
                                                key={org.value}
                                                value={org.value}
                                                label={this.renderOrgNavLabel(org)}
                                                onClick={() =>
                                                    this.props.setOrganization(org.value)
                                                }
                                                isActive={
                                                    this.props.selectedOrganization === org.value
                                                }
                                            />
                                        ))}
                                    </React.Fragment>
                                ))}
                            </Navigation>
                        </ScrollingSidebarPanel>
                        <WorkspacePanel>
                            <ComposableDataTable condensed>
                                <Row header>
                                    <Cell className="ef5-impersonate-modal__col ef5-impersonate-modal__col--primary">
                                        Name
                                    </Cell>
                                    <Cell className="ef5-impersonate-modal__col ef5-impersonate-modal__col--primary">
                                        Email
                                    </Cell>
                                    <Cell className="ef5-impersonate-modal__col ef5-impersonate-modal__col--primary">
                                        Role
                                    </Cell>
                                    <Cell className="ef5-impersonate-modal__col ef5-impersonate-modal__col--action" />
                                </Row>
                                {selectedOrganization && (
                                    <UserRow
                                        selectedOrganization={selectedOrganization}
                                        currentUserId={this.props.currentUserId}
                                        realPersonOrganization={this.props.realPersonOrganization}
                                        realPersonId={this.props.realPersonId}
                                        isImpersonating={this.props.isImpersonating}
                                        userOrganizationId={this.props.userOrganizationId}
                                        unImpersonate={this.props.unimpersonate}
                                        impersonate={this.props.impersonate}
                                        handleClose={this.handleClose}
                                        filter={this.state.filter}
                                    />
                                )}
                            </ComposableDataTable>
                        </WorkspacePanel>
                    </PanelsLayout>
                </div>
            );
        }

        return (
            <GlobalModal
                title="Impersonation"
                className="ef5-impersonate-modal"
                isOpen={this.props.isModalOpen}
                onClose={this.handleClose}
                bottomBar={<NeutralButton text="Close" onClick={this.handleClose} />}
            >
                {contents}
            </GlobalModal>
        );
    }
}

export default connect(select)(Impersonate);
